import { getPropertyEquivalentsMap } from './constants';
import groupBy from 'lodash/groupBy';

export type EquivalentPropertyFamily = {
  // Property types that were actually present in the current input.
  observedPropertyTypes: string[];
  // Full logical/physical family for that input group.
  familyPropertyTypes: Set<string>;
};

// Returns mapped equivalents only, without re-adding the original property type.
export function getEquivalentSiblings(propertyType: string): string[] {
  return Array.from(getPropertyEquivalentsMap()[propertyType] ?? []);
}

// Returns the complete equivalence family, including the original property type.
export function getEquivalentFamily(propertyType: string): Set<string> {
  return new Set([propertyType, ...getEquivalentSiblings(propertyType)]);
}

// Produces a stable grouping key so any member of the same family resolves to the same id.
export function getEquivalentFamilyId(familyMembers: Iterable<string>): string {
  return Array.from(familyMembers).sort().join('|');
}

// Groups observed property types by family and keeps both the observed subset and full family.
export function getEquivalentFamilies(
  propertyTypes: Iterable<string>,
): EquivalentPropertyFamily[] {
  const observedPropertyTypes = Array.from(propertyTypes);

  return Object.values(
    groupBy(observedPropertyTypes, (propertyType) =>
      getEquivalentFamilyId(getEquivalentFamily(propertyType)),
    ),
  )
    .map((observedFamilyPropertyTypes) => ({
      observedPropertyTypes: observedFamilyPropertyTypes,
      familyPropertyTypes: getEquivalentFamily(observedFamilyPropertyTypes[0]),
    }))
    .filter(({ familyPropertyTypes }) => familyPropertyTypes.size > 1);
}
